import { useMediaQuery } from '@components/Common/DesktopOnly';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import {
  buildCardLayout,
  rendersComponent,
  tabletSelectorsOf,
  titleSelectorOf,
  type CardBelow,
  type ColumnsConfig,
} from '@components/Common/Table/cardFields';
import { TABLET_COLUMNS, TABLET_QUERY } from '@components/Common/Table/constants';
import { TruncatingTableCell } from '@components/Common/OverflowTooltip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import {
    Box,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';
import './index.scss';

interface PaginationProps {
  enabled: boolean;
  page: number;
  rowsPerPage: number;
  totalNumberOfRows: number;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
}

interface TableProps {
  rows: any[];
  selectors: string[];
  tableHeadRow: Record<string, React.ReactNode>;
  tableBodyRow: Record<string, (row: any, index: number) => React.ReactNode>;
  pagination?: PaginationProps;
  onRowClick?: (row: any) => void;
  /**
   * Per-column say over the responsive layouts: which part each column plays on the phone card
   * (`card`) and which columns the tablet table keeps (`tablet`). Optional, and a page that does
   * not pass it gets exactly the heuristic it always had. See cardFields.ts.
   */
  columns?: ColumnsConfig;
  /** Cards below 768px (default), or below 1024px for a list whose columns do not fit a tablet. */
  cardBelow?: CardBelow;
  /** Render the matching SkeletonLoader in place of the rows. */
  loading?: boolean;
  /** The empty state's sentence. Defaults to "No records found". */
  emptyLabel?: string;
  /** A stable key per row. Defaults to the index, which is fine for a list that only re-pages. */
  getRowKey?: (row: any, index: number) => React.Key;
}

/* Re-exported: SkeletonLoader and its tests imported them from here before they moved. */
export { TABLET_COLUMNS, TABLET_QUERY };

const defaultPagination: PaginationProps = {
  enabled: false,
  page: 0,
  rowsPerPage: 10,
  totalNumberOfRows: 0,
  handleChangePage: () => {},
};

const MintlyTable: React.FC<TableProps> = ({
  rows = [],
  selectors = [],
  tableHeadRow = {},
  tableBodyRow = {},
  pagination = defaultPagination,
  onRowClick,
  columns,
  cardBelow = 'phone',
  loading = false,
  emptyLabel = 'No records found',
  getRowKey,
}) => {
  const titleSelector = titleSelectorOf(selectors, columns);
  const isClickable = !!onRowClick;
  const keyOf = (row: any, index: number): React.Key => (getRowKey ? getRowKey(row, index) : index);

  // 768–1023px: the page's tablet columns (or the first three) plus an expandable detail for the
  // remainder. Not when the page asked for cards on tablet: then this band never shows a table.
  const isTablet = useMediaQuery(TABLET_QUERY) && cardBelow !== 'tablet';
  const [expanded, setExpanded] = React.useState<React.Key | null>(null);
  const visibleSelectors = isTablet ? tabletSelectorsOf(selectors, columns) : selectors;
  const hiddenSelectors = selectors.filter((s) => !visibleSelectors.includes(s));

  if (loading) {
    return <SkeletonLoader rows={pagination.rowsPerPage || 10} columns={selectors.length} />;
  }

  const renderEmptyState = () => (
    <Box className="table-empty-state">
      <InboxOutlinedIcon className="empty-icon" />
      <Typography className="empty-label" variant="body2">
        {emptyLabel}
      </Typography>
    </Box>
  );

  /*
   * With a config, every non-title column goes to the card and the config decides its part: an
   * action column is usually unlabelled, and the label filter below is exactly what kept HR's
   * assign button off the phone. Without one, the old rule stands (a column needs a label).
   */
  const cardSelectors = selectors.filter((selector) =>
    columns ? selector !== titleSelector : selector !== titleSelector && !!tableHeadRow[selector],
  );

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    row: any,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <Box className={`mintly-table-root${cardBelow === 'tablet' ? ' cards-below-tablet' : ''}`}>
      {/* ── Mobile card list (< 768px) ── */}
      <Box className="card-list">
        {rows.length === 0 ? (
          renderEmptyState()
        ) : (
          rows.map((row, index) => {
            const title = titleSelector ? tableBodyRow[titleSelector]?.(row, index) : null;
            const { status, subtitle, facts, actions } = buildCardLayout(
              title,
              cardSelectors.map((selector) => ({
                selector,
                label: tableHeadRow[selector],
                value: tableBodyRow[selector]?.(row, index),
              })),
              columns,
            );

            return (
              <Box
                key={keyOf(row, index)}
                className={`mobile-card${isClickable ? ' clickable' : ''}`}
                aria-label="View record"
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? () => onRowClick(row) : undefined}
                onKeyDown={isClickable ? (e) => handleKeyDown(e, row) : undefined}
              >
                <Box className="mobile-card__head">
                  <span className="mobile-card__title">{title || '-'}</span>
                  {/* A page that renders its own chip keeps it, bare — wrapping
                      a chip in another pill is what squashed "Active" to "A…".
                      Plain-text statuses get the fallback pill. */}
                  {status && (
                    <span
                      className={`mobile-card__status${rendersComponent(status.value) ? ' is-bare' : ''}`}
                    >
                      {status.value}
                    </span>
                  )}
                  {isClickable && <ChevronRightIcon className="mobile-card__chevron" />}
                </Box>

                {/* Unlabelled: the company or owner names itself. */}
                {subtitle && <div className="mobile-card__subtitle">{subtitle.value}</div>}

                {/* Everything else, still all of it — two to a row rather than
                    one, and without the empty columns that used to render as a
                    labelled "-". */}
                {facts.length > 0 && (
                  <dl className="mobile-card__facts">
                    {facts.map((f) => (
                      <div key={f.selector} className="mobile-card__fact">
                        <dt className="mobile-card__fact-label">{f.label}</dt>
                        <dd className="mobile-card__fact-value">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {/* Row actions, unlabelled: the button names itself. Clicks stop here so an
                    action on a clickable card does not also open the record. */}
                {actions.length > 0 && (
                  <Box
                    className="mobile-card__actions"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {actions.map((a) => (
                      <Box key={a.selector} className="mobile-card__action" data-column={a.selector}>
                        {a.value}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {/* ── Table (≥ 768px) ── */}
      <TableContainer className="desktop-table" sx={{ overflow: 'hidden' }}>
        <Table stickyHeader aria-label="data table">
          <TableHead>
            <TableRow>
              {visibleSelectors.map((selector) => (
                <TruncatingTableCell key={selector} data-column={selector}>
                  {tableHeadRow[selector]}
                </TruncatingTableCell>
              ))}
              {hiddenSelectors.length > 0 && <TableCell className="row-detail-toggle-cell" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleSelectors.length + (hiddenSelectors.length ? 1 : 0)}
                  sx={{ border: 'none' }}
                >
                  {renderEmptyState()}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const rowKey = keyOf(row, index);
                return (
                <React.Fragment key={rowKey}>
                  <TableRow
                    className={isClickable ? 'clickable-row' : ''}
                    hover={isClickable}
                    tabIndex={isClickable ? 0 : undefined}
                    onClick={isClickable ? () => onRowClick(row) : undefined}
                    onKeyDown={isClickable ? (e) => handleKeyDown(e, row) : undefined}
                  >
                    {visibleSelectors.map((selector) => (
                      <TruncatingTableCell key={selector} data-column={selector}>
                        {tableBodyRow[selector]?.(row, index)}
                      </TruncatingTableCell>
                    ))}
                    {hiddenSelectors.length > 0 && (
                      <TableCell className="row-detail-toggle-cell">
                        <IconButton
                          size="small"
                          aria-label={expanded === rowKey ? 'Hide details' : 'Show details'}
                          aria-expanded={expanded === rowKey}
                          onClick={(e) => {
                            // The row itself navigates; expanding must not.
                            e.stopPropagation();
                            setExpanded(expanded === rowKey ? null : rowKey);
                          }}
                        >
                          {expanded === rowKey ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>

                  {hiddenSelectors.length > 0 && expanded === rowKey && (
                    <TableRow className="row-detail">
                      <TableCell colSpan={visibleSelectors.length + 1}>
                        <Box className="row-detail__grid">
                          {hiddenSelectors.map((selector) => (
                            <Box key={selector} className="row-detail__field">
                              <Typography className="row-detail__label" variant="caption">
                                {tableHeadRow[selector]}
                              </Typography>
                              <Typography className="row-detail__value" component="div" variant="body2">
                                {tableBodyRow[selector]?.(row, index) ?? '-'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      {pagination.enabled && (
        <>
          <Divider />
          <TablePagination
            component="div"
            count={Number(pagination.totalNumberOfRows)}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            rowsPerPageOptions={[]}
            sx={{
              borderTop: 'none',
              '.MuiTablePagination-toolbar': { minHeight: 48 },
            }}
            onPageChange={pagination.handleChangePage}
          />
        </>
      )}
    </Box>
  );
};

export default React.memo(MintlyTable);
